import type { DemandId } from "@/features/ryteTracker/types/goal";

export type DemandIdType = "visitId" | "VRID" | "locationPlanId" | "ISA";

export interface ResolvedDemandId {
  type: DemandIdType;
  value: string;
}

/**
 * Wire quirk, and the reason nothing here narrows with `in`: the backend emits a
 * FLAT DemandId — every variant key present, inactive ones `null`, and some
 * endpoints spell them lowercase (`vrid` / `isa`). `in` checks key existence, so
 * the union's own narrowing matches the first branch and crashes on `.id`.
 * Treat `demandId` as a best-effort record and validate the inner `.id`.
 *
 * Order = operational identity before correlation metadata. A goal carries a
 * primary demand (VRID / ISA / locationPlanId) describing the work, optionally
 * plus a `visitId` pinning it to one trailer; visitId is last because it answers
 * WHICH trailer, not WHAT work. Read order only — writes rank LP first
 * (`narrowDemandIdForWrite`).
 */
const RESOLUTION_ORDER: ReadonlyArray<{ keys: ReadonlyArray<string>; type: DemandIdType }> = [
  { keys: ["VRID", "vrid"], type: "VRID" },
  { keys: ["ISA", "isa"], type: "ISA" },
  { keys: ["locationPlanId"], type: "locationPlanId" },
  { keys: ["visitId"], type: "visitId" },
];

const readId = (
  record: Record<string, { id?: unknown } | null | undefined> | null | undefined,
  key: string
): string | undefined => {
  // `demandId` is required by the Goal type, but a wire envelope dispatched as a
  // Goal without the `transformResponse` unwrap arrives without it — and one bad
  // row must not crash the projector for the whole timeline.
  if (record == null) return undefined;
  const inner = record[key];
  if (inner == null || typeof inner.id !== "string" || inner.id.length === 0) {
    return undefined;
  }
  return inner.id;
};

/**
 * The goal's PRIMARY identity, per `RESOLUTION_ORDER` above.
 *
 * Returns `{ type: "visitId", value: "" }` when every variant is missing or
 * malformed, so a caller feeding the value into a dedup set treats the goal as
 * orphaned-but-non-fatal rather than crashing.
 */
export const resolveDemandId = (demandId: DemandId): ResolvedDemandId => {
  const record = demandId as Record<string, { id?: unknown } | null | undefined>;
  for (const { keys, type } of RESOLUTION_ORDER) {
    for (const key of keys) {
      const value = readId(record, key);
      if (value !== undefined) return { type, value };
    }
  }
  return { type: "visitId", value: "" };
};

/**
 * Every populated id, keyed by CANONICAL type regardless of wire casing (`vrid`
 * lands under `VRID`). Empty-string ids are dropped — they would otherwise
 * poison the dedup sets this feeds.
 */
export const resolveAllDemandIds = (demandId: DemandId): Partial<Record<DemandIdType, string>> => {
  const record = demandId as Record<string, { id?: unknown } | null | undefined>;
  const out: Partial<Record<DemandIdType, string>> = {};
  for (const { keys, type } of RESOLUTION_ORDER) {
    for (const key of keys) {
      const value = readId(record, key);
      if (value !== undefined) {
        out[type] = value;
        break;
      }
    }
  }
  return out;
};

/**
 * DECLARED ids only. For any "is this the same work?" comparison use
 * `collectMatchableDemandValues` instead — it adds the derived VRID and the
 * normalized spelling, without which an LP-primary goal and its own VRID-keyed
 * appointment read as different work.
 */
export const collectDemandValues = (demandId: DemandId): string[] =>
  Object.values(resolveAllDemandIds(demandId));

/**
 * Canonical casing for ONE identity token — VRID, ISA id, plan id or visitId.
 * Whitespace-only normalizes to `undefined` (absent).
 *
 * GAM's casing varies, so every DERIVED value must pass through here or the
 * appointment-, visit- and event-sourced spellings of one identity compare
 * unequal and render differently per surface. **Any new identity reader must
 * call it**, and do not narrow the name back to the VRID: under the old
 * `normalizeVrid` name the ISA / plan-id / visitId readers kept their own inline
 * `trim().toUpperCase()` instead, six copies of one rule.
 */
export const normalizeIdentityToken = (value: string | null | undefined): string | undefined => {
  const trimmed = value?.trim().toUpperCase();
  return trimmed ? trimmed : undefined;
};

/**
 * The canonical "is VRID derivation applicable?" gate — true when the goal
 * declares its own VRID or ISA.
 *
 * Must stay tied to `formatDemandId`'s ranking (VRID → ISA → derivedVrid →
 * visitId → locationPlanId): deriving for a goal that declares one of the first
 * two would hang the `+N` multi-VRID badge off a label that never shows a VRID.
 * Every consumer must read THIS, not a local copy, or the pill and the action
 * modals disagree about one goal's identity.
 */
export const declaresOperationalId = (demandId: DemandId): boolean => {
  const ids = resolveAllDemandIds(demandId);
  return Boolean(ids.VRID ?? ids.ISA);
};

/**
 * Rebuilds a create/update demand identity from canonical values, dropping the
 * lowercase aliases, null variants and empty ids the engine rejects. Emits ONE
 * primary; retains visitId whenever known. REMOVE writes share this contract.
 *
 * **Write precedence is deliberately NOT read precedence** — LP > VRID > ISA
 * here, VRID > ISA > LP > visitId in `resolveDemandId`. The plan id is the
 * planning layer's handle for the work and does not move, while a haul
 * association can be re-attached mid-visit. Do not "align" the two.
 *
 * Accepted consequence: `declaresOperationalId` is false for an LP-primary goal,
 * so all 11 label surfaces read the DERIVED VRID instead of a declared one. The
 * pill still shows `VR-…`, but only once an appointment or visit has reported the
 * plan→VRID pairing.
 */
export const narrowDemandIdForWrite = (demandId: DemandId): DemandId | null => {
  const ids = resolveAllDemandIds(demandId);
  const visitId = ids.visitId === undefined ? {} : { visitId: { id: ids.visitId } };

  if (ids.locationPlanId !== undefined) {
    return { locationPlanId: { id: ids.locationPlanId }, ...visitId };
  }
  if (ids.VRID !== undefined) return { VRID: { id: ids.VRID }, ...visitId };
  if (ids.ISA !== undefined) return { ISA: { id: ids.ISA }, ...visitId };
  if (ids.visitId !== undefined) return { visitId: { id: ids.visitId } };
  return null;
};
